import { Component, OnInit } from "@angular/core";
import { ColumnMapping, DataFilter } from "../../core/components/dynamic-table/dynamic-table.component";
import { CardItemMapping } from "@core/components/dynamic-cards/dynamic-cards.component";
import { CompanyV2Service } from "@core/services/company-v2.service";
import { CompaniesSearchService } from "src/app/services/companies-search/companies-search.service";
import { TabUtilsService } from "src/app/services/tab-utils.service";
import { BehaviorSubject, Subscription, takeUntil } from "rxjs";
import { GlobalModalService } from "@core/services/global-modal.service";
import { RouterService } from "src/app/services/router/router.service";
import { Nullable } from "primeng/ts-helpers";
import { DeviceDetectorService } from "ngx-device-detector";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "@core/auth/auth.service";
import { UserRole } from "@core/models/user";
import { VotingService } from "@core/services/voting.service";
import { AnalyticsWidgetService } from "src/app/services/analytics-widget/analytics-widget.service";
import { InvestorTab } from "@core/models/types";
import { MessageService } from "primeng/api";

@Component({
  selector: 'bpc-ic',
  templateUrl: './ic.component.html',
  styleUrl: './ic.component.scss'
})
export class IcComponent {
  companies: CardItemMapping[] | any = [];
  companiesColumns!: ColumnMapping[];

  activeVotingColumns: ColumnMapping[] = [
    { header: "Company", showHeader: false, mappedTo: "nameColumn", case: "company", },
    { header: "Check Size", showHeader: true, mappedTo: "checkSize"},
    { header: "IC Ask", showHeader: true, mappedTo: "icAsk"},
    { header: "Deal Team", showHeader: true, mappedTo: "dealTeam" },
    { header: "IC Date", showHeader: true, mappedTo: "createDate", editable: this.isInvestmentCommitteeAdmin ?  true : false, editAction: "editICDateAdded", childTab: 0},
    { header: "Comments", showHeader: true, mappedTo: "companyWorkflowCommentsLength", openLink: "comments"},
    { header: "Voting", showHeader: true, mappedTo: "", openLink: "stats"},
  ];

  archiveVotingColumns: ColumnMapping[] = [
    { header: "Company", showHeader: false, mappedTo: "nameColumn", case: "company",},
    { header: "Check Size", showHeader: true, mappedTo: "checkSize"},
    { header: "IC Ask", showHeader: true, mappedTo: "icAsk"},
    { header: "Deal Team", showHeader: true, mappedTo: "dealTeam"},
    { header: "IC Date", showHeader: true, mappedTo: "createDate", editable: this.isInvestmentCommitteeAdmin ?  true : false, editAction: "editICDateAdded", childTab: 1},
    { header: "Comments", showHeader: true, mappedTo: "companyWorkflowCommentsLength", openLink: "comments"},
    { header: "Summary", showHeader: true, mappedTo: "", openLink: "summary"},
  ];

  childTabs: any[] =[
    {label: "Active", route: "", index: 0},
    {label: "Archive", route: "history", index: 1, accessRoles: ["admin", "votercommittee", "investmentcommitteeadmin"]},
  ]

  companiesFilter: DataFilter = {
    pageNumber: 1,
    pageSize: 100,
    sortDirection: "desc",
    sortProperty: "votingDate",
  };

  totalCount = 0;
  selectedElevationFilter = "";
  type : string | null = "";
  showWeeklyData = true;
  isLoading = false;
  filterByMyElevations = false;
  showDownloadSpreadsheet = false;

  private refreshDataSubscription!: Subscription;

  get isMobile(): boolean {
    return this.deviceDetectorService.isMobile();
  }

  get isInvestmentCommitteeAdmin() {
    return this.authService.investmentcommitteeadmin;
  }

  constructor(
    private companyV2Service: CompanyV2Service, 
    private companiesSearchService: CompaniesSearchService,
    private tabUtilsService: TabUtilsService,
    private deviceDetectorService: DeviceDetectorService,
    public authService: AuthService,
    public votingService: VotingService,
    public routerService: RouterService,
    private analyticsWidgetService: AnalyticsWidgetService,
    private messageService: MessageService,
  ) {}

  protected showErrorToastMessage(message: string) {
    this.messageService.add({ key: "home-bc", severity: "error", detail: message, life: 10_000 });
  }
  protected showSuccessToastMessage(message: string) {
    this.messageService.add({ key: "home-bc", severity: "success", detail: message, life: 5_000 });
  }

  canAccessTab(roles: UserRole[]): boolean {
    return roles ? roles.some(role => this.authService.userRoles.includes(role)) : true;
  }
  
  ngOnInit(): void {
    this.routerService.updateCurrentTab(InvestorTab.VOTING);
    const url = this.routerService.url;

    if (url) {
      this.type = url.substring(url.lastIndexOf('/') + 1) ?? "";
    }
    
    this.companies = [];
    this.refreshDataSubscription = this.tabUtilsService.refreshData.subscribe(() => {
      this.loadData();
    });
    
    this.loadData();
  }

  loadData(refreshData = true) {
    if (this.isLoading) {
      return;
    }
    switch (this.type) {
      case "history":
        this.companiesColumns = this.archiveVotingColumns;
        this.routerService.currentChildIndex = 1;
        this.fetchHistory(refreshData);
        this.showDownloadSpreadsheet = true;
        break;
      case "combined":
        this.companiesFilter = this.analyticsWidgetService.icFilter;
        this.companiesColumns = this.archiveVotingColumns;
        this.routerService.currentChildIndex = 1;
        this.fetchHistory(refreshData);
        break;
      default: 
        this.type = "";
        this.companiesColumns = this.activeVotingColumns;
        this.routerService.currentChildIndex = 0;
        this.fetchCompanies(refreshData);
        break;
    }
  }

  fetchCompanies(refreshData = true) {
    this.isLoading = true;

    this.companiesSearchService.getICVotingCompaniesV2(this.companiesFilter)
      .pipe(takeUntil(this.routerService.destroy$))
      .subscribe({
      next: (response) => {
        if (response?.data?.data) {
          this.tabUtilsService.currentTotal = response?.data.totalCount;
          const newCompanies = this.companyV2Service.transformActiveICData(response.data.data);
          
          if (refreshData) {
            this.companies = [...newCompanies];
          } else this.companies.push(...newCompanies);

          this.totalCount = response.data.totalCount;
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        if (this.companiesFilter.pageNumber) this.companiesFilter.pageNumber = this.companiesFilter.pageNumber - 1;
      },
      complete: () => {
      },
    });
  }

  fetchHistory(refreshData = true) {
    this.isLoading = true;

    this.companiesSearchService.getICVotingHistoryCompaniesV2(this.companiesFilter)
      .pipe(takeUntil(this.routerService.destroy$))
      .subscribe({
      next: (response) => {
        if (response?.data?.data) {
          this.tabUtilsService.currentTotal = response?.data.totalCount;
          const newCompanies = this.companyV2Service.transformArchiveICData(response.data.data);
          
          if (refreshData) {
            this.companies = [...newCompanies];
          } else this.companies.push(...newCompanies);

          this.totalCount = response.data.totalCount;
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.showErrorToastMessage("Failed to load data, please refresh!");
        this.companies = [];
        if (this.companiesFilter.pageNumber) this.companiesFilter.pageNumber = this.companiesFilter.pageNumber - 1;
      },
      complete: () => {
      },
    });
  }

  loadMoreData() {
    if (this.companiesFilter.pageNumber) {
      this.companiesFilter.pageNumber = this.companiesFilter.pageNumber + 1;
      this.loadData(false);
    }
  }

  onFilterChange(filters: any) {
    this.companies = [];
    this.companiesFilter = { ...this.companiesFilter, ...filters };
    if (filters.pageNumber) {
      this.companiesFilter.pageNumber = filters.pageNumber;
    } else {
      this.companiesFilter.pageNumber = 1;
    }
    this.loadData();
  }

  ngOnDestroy(): void {
    if (this.refreshDataSubscription) {
      this.refreshDataSubscription.unsubscribe();
    }
  }
}
